import {
  CheckIcon,
  ImageIcon,
  PackageIcon,
  PlusIcon,
  Snowflake,
  Trash2Icon,
  XIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import useSWR from 'swr'

import RecipeImageCredit from '../components/RecipeImageCredit'
import { api } from '../lib/password'
import {
  toggleRecipeImageSize,
  useRecipeImageSize,
} from '../lib/recipeImageSizePrefs'
import type { RecipeImageSuggestion } from '../lib/recipeImageSuggestions'
import { fetcher } from '../lib/swr'
import type { FullRecipe, Product } from './types'

const COURSES = [
  'hoofdgerecht',
  'slaatje',
  'diepvries gerecht',
  'voorgerecht',
  'dessert',
  'bijgerecht',
  'ontbijt',
  'snack',
]

const UNITS = ['stuk', 'gram', 'blik', 'ml', 'pot', 'doos', 'pak']

type DraftIngredient = {
  key: string
  quantity: string
  unit: string
  display: string
  freezer: boolean
  isInStorage: boolean
  productId?: number
}

type Draft = {
  display: string
  course: string
  main: string
  portionsEat: string
  portionsFreezer: string
  preparationMinutes: string
  instructions: string
  ingredients: DraftIngredient[]
}

function toDraft(recipe: FullRecipe): Draft {
  return {
    display: recipe.display || '',
    course: recipe.course || 'hoofdgerecht',
    main: recipe.main || '',
    portionsEat: recipe.portionsEat != null ? String(recipe.portionsEat) : '2',
    portionsFreezer:
      recipe.portionsFreezer != null ? String(recipe.portionsFreezer) : '0',
    preparationMinutes:
      recipe.preparationMinutes != null
        ? String(recipe.preparationMinutes)
        : '',
    instructions: recipe.instructions || '',
    ingredients: (recipe.ingredients || []).map((ing, i) => ({
      key: `i-${ing.id}-${i}`,
      quantity: ing.quantity != null ? String(ing.quantity) : '',
      unit: ing.product.unit || '',
      display: ing.product.display || '',
      freezer: Boolean(ing.freezer),
      isInStorage: Boolean(ing.product.isInStorage),
      productId: ing.product.id,
    })),
  }
}

function emptyIngredient(): DraftIngredient {
  return {
    key: `n-${Math.random().toString(36).slice(2, 9)}`,
    quantity: '',
    unit: 'gram',
    display: '',
    freezer: false,
    isInStorage: false,
  }
}

function imageSourceLabel(
  source: string | null,
  photographer: string | null
): string | null {
  if (source === 'pexels') {
    const who = photographer?.trim()
    return who ? `${who} · Pexels` : 'Pexels'
  }
  if (source === 'themealdb') return 'TheMealDB'
  return photographer?.trim() || null
}

function suggestionCaption(suggestions: RecipeImageSuggestion[]): string {
  const hasMealDb = suggestions.some((s) => s.source !== 'pexels')
  const hasPexels = suggestions.some((s) => s.source === 'pexels')
  if (hasMealDb && hasPexels) {
    return 'TheMealDB- en Pexels-suggesties · of upload zelf'
  }
  if (hasPexels) return 'Pexels-suggesties · of upload zelf'
  if (hasMealDb) return 'TheMealDB-suggesties · of upload zelf'
  return 'Geen suggesties · upload een afbeelding'
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const n = Number(trimmed.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

function autoGrowTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

export default function RecipeEditor({
  recipe,
  onSaved,
  onCancel,
  onArchived,
  onImageChange,
}: {
  recipe: FullRecipe
  onSaved: (recipe: FullRecipe) => void
  onCancel: () => void
  onArchived?: () => void
  onImageChange?: (
    hasImage: boolean,
    imageSource?: string | null,
    attribution?: {
      photographer: string | null
      photographerUrl: string | null
      pageUrl: string | null
    }
  ) => void
}) {
  const [draft, setDraft] = useState(() => toDraft(recipe))
  const [saving, setSaving] = useState(false)
  const [hasImage, setHasImage] = useState(Boolean(recipe.hasImage))
  const [imageSource, setImageSource] = useState<string | null>(
    recipe.imageSource ?? null
  )
  const [imagePhotographer, setImagePhotographer] = useState<string | null>(
    recipe.imagePhotographer ?? null
  )
  const [imagePhotographerUrl, setImagePhotographerUrl] = useState<
    string | null
  >(recipe.imagePhotographerUrl ?? null)
  const [imagePageUrl, setImagePageUrl] = useState<string | null>(
    recipe.imagePageUrl ?? null
  )
  const [imageRev, setImageRev] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [fallbackLoaded, setFallbackLoaded] = useState(false)
  const [mealSuggestions, setMealSuggestions] = useState<
    RecipeImageSuggestion[]
  >([])
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const imageSize = useRecipeImageSize()
  const imageSmall = imageSize === 'small'
  const titleRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const imageSrc = `/api/recipes/${recipe.id}/image?v=${imageRev}`

  useEffect(() => {
    setDraft(toDraft(recipe))
    setHasImage(Boolean(recipe.hasImage))
    setImageSource(recipe.imageSource ?? null)
    setImagePhotographer(recipe.imagePhotographer ?? null)
    setImagePhotographerUrl(recipe.imagePhotographerUrl ?? null)
    setImagePageUrl(recipe.imagePageUrl ?? null)
    setFallbackLoaded(false)
    setMealSuggestions([])
  }, [recipe.id])

  useEffect(() => {
    const t = window.setTimeout(() => titleRef.current?.focus(), 80)
    return () => window.clearTimeout(t)
  }, [])

  useEffect(() => {
    autoGrowTextarea(titleRef.current)
  }, [draft.display])

  // Debounced TheMealDB suggestions while editing a recipe without an image.
  useEffect(() => {
    if (hasImage || fallbackLoaded) {
      setMealSuggestions([])
      setSuggestionsLoading(false)
      return
    }
    const q = draft.display.trim()
    if (q.length < 2) {
      setMealSuggestions([])
      setSuggestionsLoading(false)
      return
    }
    const ac = new AbortController()
    setSuggestionsLoading(true)
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(
            `/api/recipes/image-suggestions?q=${encodeURIComponent(q)}`,
            { credentials: 'include', signal: ac.signal }
          )
          if (!res.ok) {
            if (!ac.signal.aborted) setMealSuggestions([])
            return
          }
          const data = (await res.json()) as RecipeImageSuggestion[]
          if (!ac.signal.aborted) {
            setMealSuggestions(Array.isArray(data) ? data.slice(0, 4) : [])
          }
        } catch {
          if (!ac.signal.aborted) setMealSuggestions([])
        } finally {
          if (!ac.signal.aborted) setSuggestionsLoading(false)
        }
      })()
    }, 400)
    return () => {
      ac.abort()
      window.clearTimeout(t)
    }
  }, [draft.display, hasImage, fallbackLoaded])

  const showImage = hasImage || fallbackLoaded
  const sourceLabel = imageSourceLabel(imageSource, imagePhotographer)

  const onImageLoad = () => {
    if (hasImage) return
    setFallbackLoaded(true)
    setHasImage(true)
    // Don't assume TheMealDB — user uploads and auto-cache both hit this path.
    void fetch(`/api/recipes/${recipe.id}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((r) => {
        const source = (r?.imageSource as string | null | undefined) ?? null
        const photographer =
          (r?.imagePhotographer as string | null | undefined) ?? null
        const photographerUrl =
          (r?.imagePhotographerUrl as string | null | undefined) ?? null
        const pageUrl = (r?.imagePageUrl as string | null | undefined) ?? null
        setImageSource(source)
        setImagePhotographer(photographer)
        setImagePhotographerUrl(photographerUrl)
        setImagePageUrl(pageUrl)
        onImageChange?.(true, source, {
          photographer,
          photographerUrl,
          pageUrl,
        })
      })
      .catch(() => {
        setImageSource(null)
        setImagePhotographer(null)
        setImagePhotographerUrl(null)
        setImagePageUrl(null)
        onImageChange?.(true, null, {
          photographer: null,
          photographerUrl: null,
          pageUrl: null,
        })
      })
  }

  const update = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  const updateIngredient = (key: string, patch: Partial<DraftIngredient>) => {
    setDraft((d) => ({
      ...d,
      ingredients: d.ingredients.map((ing) =>
        ing.key === key ? { ...ing, ...patch } : ing
      ),
    }))
  }

  const removeIngredient = (key: string) => {
    setDraft((d) => ({
      ...d,
      ingredients: d.ingredients.filter((ing) => ing.key !== key),
    }))
  }

  const uploadImage = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Kies een afbeeldingsbestand')
      return
    }
    setUploading(true)
    try {
      const res = await fetch(`/api/recipes/${recipe.id}/image`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data.message || 'Upload mislukt')
        return
      }
      setHasImage(true)
      setImageSource(null)
      setImagePhotographer(null)
      setImagePhotographerUrl(null)
      setImagePageUrl(null)
      setFallbackLoaded(false)
      setMealSuggestions([])
      setImageRev((n) => n + 1)
      onImageChange?.(true, null, {
        photographer: null,
        photographerUrl: null,
        pageUrl: null,
      })
      toast.success('Afbeelding gezet')
    } finally {
      setUploading(false)
    }
  }

  const applyMealSuggestion = async (thumbUrl: string, mealName: string) => {
    if (uploading) return
    setUploading(true)
    try {
      const res = await fetch(`/api/recipes/${recipe.id}/image/themealdb`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thumbUrl }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data.message || 'Suggestie toepassen mislukt')
        return
      }
      setHasImage(true)
      setImageSource('themealdb')
      setImagePhotographer(null)
      setImagePhotographerUrl(null)
      setImagePageUrl(null)
      setFallbackLoaded(false)
      setMealSuggestions([])
      setImageRev((n) => n + 1)
      onImageChange?.(true, 'themealdb', {
        photographer: null,
        photographerUrl: null,
        pageUrl: null,
      })
      toast.success(mealName ? `Afbeelding: ${mealName}` : 'Afbeelding gezet')
    } finally {
      setUploading(false)
    }
  }

  const applyPexelsSuggestion = async (s: RecipeImageSuggestion) => {
    if (uploading) return
    const downloadUrl = s.downloadUrl?.trim()
    if (!downloadUrl) {
      toast.error('Pexels-suggestie ongeldig')
      return
    }
    setUploading(true)
    try {
      const res = await fetch(`/api/recipes/${recipe.id}/image/pexels`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          downloadUrl,
          photographer: s.photographer,
          photographerUrl: s.photographerUrl,
          pageUrl: s.pageUrl,
        }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        message?: string
        imagePhotographer?: string | null
        imagePhotographerUrl?: string | null
        imagePageUrl?: string | null
      }
      if (!res.ok) {
        toast.error(data.message || 'Suggestie toepassen mislukt')
        return
      }
      const photographer = data.imagePhotographer ?? s.photographer ?? null
      const photographerUrl =
        data.imagePhotographerUrl ?? s.photographerUrl ?? null
      const pageUrl = data.imagePageUrl ?? s.pageUrl ?? null
      setHasImage(true)
      setImageSource('pexels')
      setImagePhotographer(photographer)
      setImagePhotographerUrl(photographerUrl)
      setImagePageUrl(pageUrl)
      setFallbackLoaded(false)
      setMealSuggestions([])
      setImageRev((n) => n + 1)
      onImageChange?.(true, 'pexels', {
        photographer,
        photographerUrl,
        pageUrl,
      })
      toast.success(
        photographer ? `Afbeelding: ${photographer}` : 'Afbeelding gezet'
      )
    } finally {
      setUploading(false)
    }
  }

  const applySuggestion = (s: RecipeImageSuggestion) => {
    if (s.source === 'pexels') void applyPexelsSuggestion(s)
    else void applyMealSuggestion(s.thumbUrl, s.name)
  }

  const clearImage = async () => {
    if (!hasImage && !fallbackLoaded) return
    setUploading(true)
    try {
      const res = await fetch(`/api/recipes/${recipe.id}/image`, {
        method: 'DELETE',
        credentials: 'include',
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data.message || 'Verwijderen mislukt')
        return
      }
      setHasImage(false)
      setImageSource(null)
      setImagePhotographer(null)
      setImagePhotographerUrl(null)
      setImagePageUrl(null)
      setFallbackLoaded(false)
      setImageRev((n) => n + 1)
      onImageChange?.(false, null, {
        photographer: null,
        photographerUrl: null,
        pageUrl: null,
      })
      toast.success('Afbeelding verwijderd')
    } finally {
      setUploading(false)
    }
  }

  const onDropFiles = (files: FileList | null) => {
    const file = files?.[0]
    if (file) void uploadImage(file)
  }

  const save = async () => {
    if (saving) return
    if (!draft.display.trim()) {
      toast.error('Geef het recept een naam')
      titleRef.current?.focus()
      return
    }
    setSaving(true)
    try {
      const { data } = await api.patch<FullRecipe>(
        `/api/recipes/${recipe.id}`,
        {
          display: draft.display.trim(),
          course: draft.course.trim() || null,
          main: draft.main.trim() || null,
          portionsEat: parseOptionalNumber(draft.portionsEat) ?? 2,
          portionsFreezer: parseOptionalNumber(draft.portionsFreezer) ?? 0,
          preparationMinutes: parseOptionalNumber(draft.preparationMinutes),
          instructions: draft.instructions.trim() || null,
          ingredients: draft.ingredients
            .filter((ing) => ing.display.trim())
            .map((ing) => ({
              quantity: parseOptionalNumber(ing.quantity),
              freezer: ing.freezer,
              product: {
                id: ing.productId,
                display: ing.display.trim(),
                unit: ing.unit.trim(),
                isInStorage: ing.isInStorage,
              },
            })),
        }
      )
      toast.success('Recept opgeslagen')
      onSaved(data)
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Opslaan mislukt'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const archive = async () => {
    if (!confirm('Recept archiveren? Het verdwijnt uit het kookboek.')) return
    setSaving(true)
    try {
      await api.delete(`/api/recipes/${recipe.id}`)
      toast.success('Recept gearchiveerd')
      onArchived?.()
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Archiveren mislukt'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void save()
      }}
      className={
        'animate-[cookbook-in_0.35s_ease-out_both] flex flex-col -mx-4 md:-mx-6 -mt-1 -mb-8 ' +
        'h-[calc(var(--silk-100-lvh-dvh-pct,100dvh)-2.5rem)] ' +
        'max-h-[calc(var(--silk-100-lvh-dvh-pct,100dvh)-2.5rem)] ' +
        'pointer-coarse:h-[calc(var(--silk-100-lvh-dvh-pct,100dvh)-8rem-max(env(safe-area-inset-bottom,0px),var(--expo-sab,0px)))] ' +
        'pointer-coarse:max-h-[calc(var(--silk-100-lvh-dvh-pct,100dvh)-8rem-max(env(safe-area-inset-bottom,0px),var(--expo-sab,0px)))]'
      }
    >
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 md:px-6 pb-4">
        <div className="text-sm font-medium text-lime-800/70 mb-3 tracking-wide">
          Recept bewerken
        </div>

        <div
          className={
            showImage && imageSmall ? 'flex items-start gap-3' : undefined
          }
        >
          {showImage && imageSmall ? (
            <button
              type="button"
              title="Klik voor grotere afbeelding"
              aria-label="Afbeeldingsgrootte wisselen"
              onClick={() => toggleRecipeImageSize()}
              className="mt-1 shrink-0 overflow-hidden rounded-lg ring-1 ring-black/10 outline-none focus-visible:ring-2 focus-visible:ring-lime-300"
            >
              <img
                src={imageSrc}
                alt=""
                className="block h-[50px] w-[50px] object-cover"
                onLoad={onImageLoad}
                onError={() => setFallbackLoaded(false)}
              />
            </button>
          ) : null}
          <textarea
            ref={titleRef}
            rows={1}
            value={draft.display}
            onChange={(e) => {
              update('display', e.target.value)
              autoGrowTextarea(e.target)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                e.currentTarget.form?.requestSubmit()
              }
            }}
            placeholder="Naam van het recept"
            className="w-full min-w-0 flex-1 font-bold text-3xl lg:text-4xl xl:text-5xl leading-tight bg-transparent border-0 border-b-2 border-transparent focus:border-lime-300 outline-none placeholder:text-gray-300 transition-colors pb-1 resize-none overflow-hidden"
          />
        </div>

        <div className="mt-5">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              onDropFiles(e.target.files)
              e.target.value = ''
            }}
          />
          {!showImage ? (
            <div className="space-y-1.5">
              <div
                className={
                  (uploading ? 'opacity-60 pointer-events-none ' : '') +
                  'flex gap-2 overflow-x-auto pb-0.5'
                }
                role="list"
                aria-label="Afbeeldingssuggesties"
              >
                {mealSuggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="listitem"
                    title={
                      s.source === 'pexels' && s.photographer
                        ? `${s.name} · ${s.photographer} · Pexels`
                        : s.name
                    }
                    aria-label={`Gebruik afbeelding: ${s.name}`}
                    disabled={uploading}
                    onClick={() => applySuggestion(s)}
                    className="group relative h-16 w-16 shrink-0 overflow-hidden rounded-lg ring-1 ring-black/10 bg-stone-100 outline-none transition hover:ring-lime-400 focus-visible:ring-2 focus-visible:ring-lime-300 disabled:opacity-50"
                  >
                    <img
                      src={s.thumbUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                    <span className="pointer-events-none absolute inset-0 bg-lime-900/0 transition group-hover:bg-lime-900/10" />
                  </button>
                ))}
                <button
                  type="button"
                  role="listitem"
                  aria-label="Receptafbeelding uploaden"
                  disabled={uploading}
                  onClick={() => !uploading && fileRef.current?.click()}
                  onDragEnter={(e) => {
                    e.preventDefault()
                    setDragOver(true)
                  }}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setDragOver(true)
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault()
                    setDragOver(false)
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    setDragOver(false)
                    onDropFiles(e.dataTransfer.files)
                  }}
                  className={
                    (dragOver
                      ? 'border-lime-500 bg-lime-50 ring-2 ring-lime-200 '
                      : 'border-gray-200 hover:border-lime-300 hover:bg-lime-50/50 ') +
                    'flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border-2 border-dashed outline-none transition focus-visible:ring-2 focus-visible:ring-lime-300 disabled:opacity-50'
                  }
                >
                  <ImageIcon className="h-5 w-5 text-lime-800/55" />
                  <span className="text-[10px] font-medium leading-none text-lime-950/55">
                    {uploading ? '…' : 'Upload'}
                  </span>
                </button>
              </div>
              <p className="text-[11px] text-black/35">
                {suggestionsLoading
                  ? 'Suggesties laden…'
                  : suggestionCaption(mealSuggestions)}
              </p>
            </div>
          ) : imageSmall ? (
            <div
              onDragEnter={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={(e) => {
                e.preventDefault()
                setDragOver(false)
              }}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                onDropFiles(e.dataTransfer.files)
              }}
              className={
                (dragOver
                  ? 'border-lime-500 bg-lime-50 ring-2 ring-lime-200 '
                  : 'border-gray-200 ') +
                (uploading ? 'opacity-60 pointer-events-none ' : '') +
                'flex flex-wrap items-center gap-2 rounded-xl border border-dashed px-3 py-2.5 transition-colors'
              }
            >
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="rounded-full bg-lime-100 text-lime-900 hover:bg-lime-200 px-3 py-1 text-xs font-medium disabled:opacity-50"
              >
                Vervangen
              </button>
              <button
                type="button"
                disabled={uploading}
                onClick={() => void clearImage()}
                className="rounded-full bg-red-50 text-red-800 hover:bg-red-100 px-3 py-1 text-xs font-medium disabled:opacity-50"
              >
                Verwijderen
              </button>
              <span className="text-xs text-black/40">
                {uploading
                  ? 'Bezig…'
                  : sourceLabel
                    ? `${sourceLabel} · of sleep een nieuw bestand`
                    : 'of sleep een nieuw bestand hierheen'}
              </span>
            </div>
          ) : (
            <div
              onDragEnter={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={(e) => {
                e.preventDefault()
                setDragOver(false)
              }}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                onDropFiles(e.dataTransfer.files)
              }}
              className={
                (dragOver ? 'ring-2 ring-lime-300 ' : '') +
                (uploading ? 'opacity-60 pointer-events-none ' : '') +
                'relative w-full overflow-hidden rounded-2xl ring-1 ring-black/5 bg-stone-100 aspect-[16/9] transition'
              }
            >
              <button
                type="button"
                title="Klik voor kleinere afbeelding"
                aria-label="Afbeeldingsgrootte wisselen"
                onClick={() => toggleRecipeImageSize()}
                className="absolute inset-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lime-300"
              >
                <img
                  src={imageSrc}
                  alt=""
                  className="h-full w-full object-cover"
                  onLoad={onImageLoad}
                  onError={() => setFallbackLoaded(false)}
                />
              </button>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/55 to-transparent px-3 py-2.5">
                <span className="text-sm text-white/90 font-medium">
                  {uploading
                    ? 'Bezig…'
                    : sourceLabel
                      ? `${sourceLabel} · klik voor klein · sleep om te vervangen`
                      : 'Klik voor klein · sleep om te vervangen'}
                </span>
                <div className="pointer-events-auto flex items-center gap-2">
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    className="rounded-full bg-white/90 text-lime-900 hover:bg-white px-3 py-1 text-xs font-medium disabled:opacity-50"
                  >
                    Vervangen
                  </button>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => void clearImage()}
                    className="rounded-full bg-white/90 text-red-800 hover:bg-white px-3 py-1 text-xs font-medium disabled:opacity-50"
                  >
                    Verwijderen
                  </button>
                </div>
              </div>
            </div>
          )}
          {showImage ? (
            <RecipeImageCredit
              source={imageSource}
              photographer={imagePhotographer}
              photographerUrl={imagePhotographerUrl}
              pageUrl={imagePageUrl}
            />
          ) : null}
        </div>

        <div className="mt-6 space-y-5">
          <Field label="Gang">
            <div className="flex flex-wrap gap-1.5">
              {COURSES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => update('course', c)}
                  className={
                    (draft.course === c
                      ? 'bg-lime-700 text-white'
                      : 'bg-lime-50 text-lime-900 hover:bg-lime-100') +
                    ' rounded-full px-3 py-1.5 text-sm font-medium capitalize transition-colors'
                  }
                >
                  {c}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Porties">
              <NumberInput
                value={draft.portionsEat}
                onChange={(v) => update('portionsEat', v)}
                min={0}
              />
            </Field>
            <Field label="Invriesporties">
              <NumberInput
                value={draft.portionsFreezer}
                onChange={(v) => update('portionsFreezer', v)}
                min={0}
              />
            </Field>
            <Field label="Minuten" className="col-span-2 sm:col-span-2">
              <NumberInput
                value={draft.preparationMinutes}
                onChange={(v) => update('preparationMinutes', v)}
                min={0}
                placeholder="—"
              />
            </Field>
          </div>

          <Field label="Type / tags">
            <input
              value={draft.main}
              onChange={(e) => update('main', e.target.value)}
              placeholder="bv. vegetarisch, pasta, soep…"
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-base outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100 transition"
            />
          </Field>

          <Field label="Ingrediënten">
            <div className="flex flex-col gap-2">
              {draft.ingredients.map((ing) => (
                <IngredientRow
                  key={ing.key}
                  ingredient={ing}
                  onChange={(patch) => updateIngredient(ing.key, patch)}
                  onRemove={() => removeIngredient(ing.key)}
                />
              ))}
              <button
                type="button"
                onClick={() =>
                  setDraft((d) => ({
                    ...d,
                    ingredients: [...d.ingredients, emptyIngredient()],
                  }))
                }
                className="mt-1 flex items-center justify-center gap-2 rounded-xl border border-dashed border-lime-300 text-lime-800 hover:bg-lime-50 py-2.5 text-sm font-medium transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Ingrediënt toevoegen
              </button>
            </div>
          </Field>

          <Field label="Instructies">
            <textarea
              value={draft.instructions}
              onChange={(e) => update('instructions', e.target.value)}
              rows={8}
              placeholder="Stap voor stap… Links mag je plakken."
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-base leading-relaxed outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100 transition resize-y min-h-[10rem]"
            />
          </Field>
        </div>

        <div className="mt-8 mb-2">
          <div className="opacity-60 text-sm mb-2">Gevarenzone</div>
          <button
            type="button"
            disabled={saving}
            onClick={archive}
            className="flex items-center gap-2 text-sm font-medium text-red-800 bg-red-50 hover:bg-red-100 py-2 pl-2 pr-4 rounded-full transition-colors disabled:opacity-50"
          >
            <Trash2Icon className="w-5 h-5" />
            Recept archiveren
          </button>
        </div>
      </div>

      <div className="shrink-0 border-t border-black/5 bg-white/95 backdrop-blur-sm px-4 md:px-6 pt-3 pb-3 pointer-coarse:pb-[max(0.75rem,env(safe-area-inset-bottom),var(--expo-sab,0px))]">
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 min-w-[8rem] flex items-center justify-center gap-2 rounded-full bg-lime-700 hover:bg-lime-800 disabled:opacity-50 text-white font-medium py-2.5 px-4 transition-colors"
          >
            <CheckIcon className="w-5 h-5" />
            Opslaan
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            className="flex items-center justify-center gap-2 rounded-full bg-lime-100 text-lime-900 hover:bg-lime-200 font-medium py-2.5 px-4 transition-colors"
          >
            Annuleren
          </button>
        </div>
      </div>
    </form>
  )
}

function Field({
  label,
  children,
  className = '',
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <div className="text-sm text-black/45 mb-1.5 font-medium">{label}</div>
      {children}
    </div>
  )
}

function NumberInput({
  value,
  onChange,
  min,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  min?: number
  placeholder?: string
}) {
  return (
    <input
      type="number"
      inputMode="decimal"
      min={min}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-base tabular-nums outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100 transition"
    />
  )
}

function IngredientRow({
  ingredient,
  onChange,
  onRemove,
}: {
  ingredient: DraftIngredient
  onChange: (patch: Partial<DraftIngredient>) => void
  onRemove: () => void
}) {
  const [suggestions, setSuggestions] = useState<Product[]>([])
  const [open, setOpen] = useState(false)
  const blurTimer = useRef<number | null>(null)

  const products = useSWR<Product[]>(
    ingredient.display.trim().length >= 1
      ? `/api/products?q=${encodeURIComponent(ingredient.display.trim())}`
      : null,
    fetcher
  )

  useEffect(() => {
    setSuggestions(products.data || [])
  }, [products.data])

  return (
    <div className="group relative rounded-xl border border-gray-200 bg-gray-50/80 hover:bg-white hover:border-lime-200 hover:shadow-sm transition-all p-2 sm:p-2.5">
      <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
        <input
          value={ingredient.quantity}
          onChange={(e) => onChange({ quantity: e.target.value })}
          placeholder="0"
          inputMode="decimal"
          className="w-16 shrink-0 rounded-lg border border-transparent bg-white/80 px-2 py-1.5 text-sm tabular-nums outline-none focus:border-lime-300 focus:ring-1 focus:ring-lime-100"
        />
        <select
          value={UNITS.includes(ingredient.unit) ? ingredient.unit : ''}
          onChange={(e) => {
            if (e.target.value === '') {
              onChange({ unit: '' })
              return
            }
            onChange({ unit: e.target.value })
          }}
          className="w-[4.5rem] shrink-0 rounded-lg border border-transparent bg-white/80 px-1.5 py-1.5 text-sm outline-none focus:border-lime-300"
        >
          {UNITS.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
          <option value="">anders</option>
        </select>
        {!UNITS.includes(ingredient.unit) ? (
          <input
            value={ingredient.unit}
            onChange={(e) => onChange({ unit: e.target.value })}
            placeholder="eenheid"
            className="w-16 shrink-0 rounded-lg border border-transparent bg-white/80 px-2 py-1.5 text-sm outline-none focus:border-lime-300"
          />
        ) : null}
        <div className="relative flex-1 min-w-[8rem]">
          <input
            value={ingredient.display}
            onChange={(e) => {
              onChange({ display: e.target.value, productId: undefined })
              setOpen(true)
            }}
            onFocus={() => {
              if (blurTimer.current) window.clearTimeout(blurTimer.current)
              setOpen(true)
            }}
            onBlur={() => {
              blurTimer.current = window.setTimeout(() => setOpen(false), 150)
            }}
            placeholder="Product"
            className="w-full rounded-lg border border-transparent bg-white/80 px-2 py-1.5 text-sm font-medium outline-none focus:border-lime-300 focus:ring-1 focus:ring-lime-100"
            autoComplete="off"
          />
          {open && suggestions.length > 0 ? (
            <ul className="absolute z-20 left-0 right-0 top-full mt-1 max-h-40 overflow-auto rounded-xl bg-white shadow-lg border border-gray-100 py-1">
              {suggestions.slice(0, 8).map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-lime-50 flex justify-between gap-2"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange({
                        display: p.display,
                        unit: p.unit || ingredient.unit,
                        productId: p.id,
                        isInStorage: Boolean(p.isInStorage),
                      })
                      setOpen(false)
                    }}
                  >
                    <span className="font-medium">{p.display}</span>
                    <span className="text-black/40 shrink-0">
                      {[p.unit, p.isInStorage ? 'voorraad' : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <button
          type="button"
          title="In voorraad"
          onClick={() => onChange({ isInStorage: !ingredient.isInStorage })}
          className={
            (ingredient.isInStorage
              ? 'bg-amber-100 text-amber-700'
              : 'bg-white/80 text-gray-300 hover:text-amber-600') +
            ' shrink-0 rounded-lg p-2 transition-colors'
          }
        >
          <PackageIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          title="Invriesbaar"
          onClick={() => onChange({ freezer: !ingredient.freezer })}
          className={
            (ingredient.freezer
              ? 'bg-blue-100 text-blue-600'
              : 'bg-white/80 text-gray-300 hover:text-blue-400') +
            ' shrink-0 rounded-lg p-2 transition-colors'
          }
        >
          <Snowflake className="w-4 h-4" />
        </button>
        <button
          type="button"
          title="Verwijderen"
          onClick={onRemove}
          className="shrink-0 rounded-lg p-2 text-gray-300 hover:text-red-600 hover:bg-red-50 transition-colors"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
